import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  BarChart,
  Psychology,
} from '@mui/icons-material';
import { DoctorProfileCard } from './DoctorProfileCard';
import { VisitTimeline } from './VisitTimeline';
import { EngagementAnalytics } from './EngagementAnalytics';
import { AIInsights } from './AIInsights';
import { useDoctor, useAIPersona } from '../hooks/useDoctor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};
export const emptyDoctor = {
  id: '',
  personalInfo: {
    name: '',
    age: 0,
    gender: '',
    photo: '',
  },
  professionalInfo: {
    specialty: '',
    subSpecialty: '',
    tier: '',
    experience: 0,
    medicalSchool: '',
    fellowships: [],
    registrationNumber: '',
  },
  practiceInfo: {
    clinic: '',
    hospital: '',
    address: '',
    territory: '',
    coordinates: {
      lat: 0,
      lng: 0,
    },
  },
  contactInfo: {
    phone: '',
    email: '',
    assistant: '',
    assistantPhone: '',
  },
  preferences: {
    preferredTime: '',
    preferredDays: [],
    preferredContactMethod: '',
    languages: [],
    communicationNotes: '',
  },
  practiceMetrics: {
    patientVolume: 0,
    avgConsultTime: 0,
    prescriptionPotential: '',
    influenceLevel: '',
  },
  productAffinities: {
    currentlyPrescribing: [],
    interested: [],
    competitor: [],
  },
};

export const Dashboard: React.FC = () => {
  const [selectedDoctorId, setSelectedDoctorId] = useState('DOC-001');
  const [activeTab, setActiveTab] = useState(0);
  const { doctor, visits } = useDoctor(selectedDoctorId);
  const { insights, loading: insightsLoading, error: insightsError, refreshInsights, lastFetched, source } = useAIPersona(selectedDoctorId);

  const handleDoctorChange = (doctorId: string) => {
    setSelectedDoctorId(doctorId);
    // setActiveTab(0); // Reset to first tab when switching doctors
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleRefreshInsights = () => {
    // Trigger refresh of AI insights
    refreshInsights && refreshInsights();
  };

  // Show loading only on initial load, not when switching doctors
  // if (loading && !doctor) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  // if (error && !doctor) {
  //   return (
  //     <Container maxWidth="lg" sx={{ mt: 4 }}>
  //       <Alert severity="error">{error || 'Doctor not found'}</Alert>
  //     </Container>
  //   );
  // }

  // if (!doctor) {
  //   return (
  //     <Container maxWidth="lg" sx={{ mt: 4 }}>
  //       <Alert severity="info">Select a doctor to view their profile</Alert>
  //     </Container>
  //   );
  // }

  // Calculate KPIs
  const totalVisits = visits.length;
  const avgDuration = visits.length > 0
    ? Math.round(visits.reduce((sum, v) => sum + v.duration, 0) / visits.length)
    : 0;
  const totalSamples = visits.reduce((sum, v) => sum + v.samplesGiven, 0);
  const positiveOutcomes = visits.filter(
    v => v.outcome === 'Very Positive' || v.outcome === 'Positive' || v.outcome === 'Committed to Try'
  ).length;
  const feedbackRate = visits.length > 0 ? Math.round((positiveOutcomes / visits.length) * 100) : 0;

  return (
    <Box sx={{ bgcolor: '#F9F5EF', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 3 }}>
        {/* Header with Doctor Selection */}
        <Paper sx={{ p: 2, mb: 3, boxShadow: "none", backgroundColor: 'transparent' }}>
          {/* <Typography variant="h4" fontWeight="bold" mb={2}>
            Doctor 360° Mini-Dashboard
          </Typography> */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {['DOC-001', 'DOC-002', 'DOC-003', 'DOC-004'].map((docId) => (
              <Paper
                key={docId}
                onClick={() => handleDoctorChange(docId)}
                sx={{
                  p: 1.5,
                  px: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: selectedDoctorId === docId ? "1px solid #C16D18" : '',
                  bgcolor: selectedDoctorId === docId ? '#FCF4CE' : 'grey.100',
                  color: selectedDoctorId === docId ? 'black' : 'text.primary',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: selectedDoctorId === docId ? 'primary.dark' : 'grey.200' },
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  {docId === 'DOC-001' ? 'Dr. Anil Mehta' :
                    docId === 'DOC-002' ? 'Dr. Sunita Patel' :
                      docId === 'DOC-003' ? 'Dr. Rajesh Pandey' :
                        'Dr. Lakshmi Krishna'}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Sidebar - Doctor Profile */}
          <Grid item xs={12} md={3}>
            <DoctorProfileCard key={selectedDoctorId} doctor={doctor ?? emptyDoctor} />
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <BarChart sx={{ color: 'primary.main', mb: 0.3 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {totalVisits}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Visits
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {avgDuration}m
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg. Duration
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {totalSamples}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Samples Given
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    textAlign: 'center',
                    boxShadow: 2,
                    bgcolor: '#ff9800',
                    color: 'white',
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: '#C16A18',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {feedbackRate}%
                  </Typography>
                  <Typography variant="caption">
                    Feedback Rate
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Paper sx={{ borderRadius: 2, boxShadow: 2 ,width:"fit-content"}}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  // borderBottom: 1,
                  paddingRight:2,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                  },
                  '& .Mui-selected': {
                    color: '#ff9800',
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: '#ff9800',
                  },
                }}
              >
                <Tab
                  icon={<TimelineIcon />}
                  iconPosition="start"
                  label="Visit Timeline"
                />
                <Tab
                  icon={<BarChart />}
                  iconPosition="start"
                  label="Analytics"
                />
                <Tab
                  icon={<Psychology />}
                  iconPosition="start"
                  label="AI Insights"
                />
              </Tabs>
              </Paper>

              <Paper sx={{ pl:4 ,backgroundColor:"#fff",marginTop:"30px" }}>
                <TabPanel value={activeTab} index={0}>
                  {
                    visits.length === 0 ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>) : (<VisitTimeline key={`visits-${selectedDoctorId}`} visits={visits} />
                    )
                  }
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <EngagementAnalytics key={`analytics-${selectedDoctorId}`} visits={visits} />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  {insightsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : insightsError ? (
                    <Alert severity="error">{insightsError}</Alert>
                  ) : insights ? (
                    <AIInsights insights={insights} onRefresh={handleRefreshInsights} lastFetched={lastFetched || undefined} source={source || undefined} />
                  ) : (
                    <Alert severity="info">No AI insights available</Alert>
                  )}
                </TabPanel>
              </Paper>
            {/* </Paper> */}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

